import { yupResolver } from '@hookform/resolvers/yup';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Card, CardActions, CardContent } from '@mui/material';
import dayjs from 'dayjs';
import useClient from 'hooks/swr/useClient';
import CardedPage from 'layout/pages/CardedPage';
import SimplePage from 'layout/pages/SimplePage/SimplePage';
import { isNil, toNumber } from 'lodash';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useNavigate, useParams } from 'react-router-dom';
import * as clientService from 'services/clientService';
import StoreContext from 'stores/context/StoreContext';
import ClientForm from '../../components/ClientForm/ClientForm';
import clientFormSchema from '../../components/ClientForm/clientFormSchema';
import ClientEditPageHeader from './ClientEditPageHeader';
import ClientPageSkeleton from './ClientPageSkeleton';
import type ClientFormData from '../../components/ClientForm/clientFormData';
import type { Client } from 'models/client/client';
import type { FC } from 'react';
import type { SubmitErrorHandler } from 'react-hook-form';

const clientToFormClient = (source?: Client): ClientFormData | undefined =>
  isNil(source)
    ? undefined
    : {
        ...source,
        docDateOfIssue: isNil(source.docDateOfIssue)
          ? null
          : dayjs(source.docDateOfIssue),
      };

const ClientEditPage: FC = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { data: client, isLoading } = useClient(toNumber(clientId));
  const { notifyStore } = useContext(StoreContext);

  const formContext = useForm<ClientFormData>({
    mode: 'onTouched',
    defaultValues: clientToFormClient(client),
    resolver: yupResolver(clientFormSchema),
  });

  useEffect(() => {
    formContext.reset(clientToFormClient(client));
  }, [client, formContext]);

  const saveClient = async (d: ClientFormData): Promise<void> => {
    try {
      await clientService.updateClientAsync(client?.id ?? 0, {
        name: d.name,
        surname: d.surname,
        patronymic: d.patronymic,
        phone: d.phone,
        email: d.email,
        registrationAddress: d.registrationAddress,
        type: d.type,
        fullName: d.fullName,
        docType: d.docType,
        docDateOfIssue: !isNil(d.docDateOfIssue)
          ? new Date(d.docDateOfIssue.toISOString())
          : undefined,
        docIssuedBy: d.docIssuedBy,
        docNumber: d.docNumber,
        docSeries: d.docSeries,

        entityAddress: d.entityAddress,
        inn: d.inn,
        kpp: d.kpp,
        ogrn: d.ogrn,
        paymentAccount: d.paymentAccount,
        managementFullName: d.managementFullName,

        bankBIC: d.bankBIC,
        bankName: d.bankName,
        bankINN: d.bankINN,
        bankKPP: d.bankKPP,
        bankCorrespondentAccount: d.bankCorrespondentAccount,
      });

      notifyStore.showNotify('Изменения сохранены', 'success');
      toView();
    } catch (err) {
      console.log(err);
    }
  };

  const saveInvalidData: SubmitErrorHandler<ClientFormData> = (): void => {
    notifyStore.showNotify(
      'Не удалось сохранить клиента. Введенные данные не корректны',
      'error'
    );
  };

  const { isSubmitting } = formContext.formState;

  const toView = (): void => {
    navigate(`/clients/${clientId ?? 0}`);
  };

  if (isLoading || isNil(client)) {
    return (
      <CardedPage title='Клиент' header={<ClientEditPageHeader />}>
        <ClientPageSkeleton />
      </CardedPage>
    );
  }

  return (
    <SimplePage title='Редактирование клиента' header={<ClientEditPageHeader />}>
      <FormContainer
        formContext={formContext}
        onSuccess={saveClient}
        onError={saveInvalidData}>
        <Card>
          <CardContent>
            <ClientForm />

            {/* <AutocompleteElement
                  required
                  autocompleteProps={{ freeSolo: true }}
                  textFieldProps={{ margin: 'normal' }}
                  label='Адрес'
                  loading={false}
                  name='registrationAddress'
                  options={[]}
                /> */}
          </CardContent>

          <CardActions sx={{ padding: 2 }}>
            <LoadingButton
              loading={isSubmitting}
              variant='contained'
              loadingPosition={'start'}
              type='submit'
              startIcon={<SaveIcon />}>
              <span>Сохранить</span>
            </LoadingButton>
            <Button
              disabled={isSubmitting}
              onClick={toView}
              variant='outlined'
              color='secondary'>
              Отмена
            </Button>
          </CardActions>
        </Card>
      </FormContainer>
    </SimplePage>
  );
};

export default ClientEditPage;
